import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { fonts } from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { Containers } from '@stories/Widgets/YourSolution/YS/Components/Shared/Step.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Wrapper = styled.div ``;
const Container = styled(Containers.FullWidth) `
  display: flex;
  flex-direction: column;
  gap: ${fluid(40, 64)};

  @media ${until(Device.TabletLarge)} {
    padding-bottom: 52px;
  }
`;
const TopSection = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
})) `
  width: 100%;
  min-height: 250px;

  @media ${until(Device.Mobile)} {
    position: relative;
    left: calc(var(--gridPadding) * -1);
    width: calc(100% + var(--gridPadding) * 2);
  }

  @media ${until(Device.Desktop)} {
    display: flex;
    flex-direction: column;
  }

  @media ${from(Device.TabletLarge)} {
    max-height: 500px;
  }

  @media ${from(Device.Desktop)} {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
  }
`;
const TopSectionLeftContent = styled(m.div).attrs(() => ({
    variants: {
        visible: {
            transition: { staggerChildren: 0.15 },
        },
    },
})) `
  background-color: ${brand.primary.alt};
  padding: ${fluid(32, 103)} ${fluid(15, 60)};
  padding-right: 0px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  justify-content: center;

  @media ${until(Device.Desktop)} {
    padding-top: 0px;
    z-index: 3;
  }

  grid-column: 1 / 7;
  @media ${from(Device.DesktopLarge)} {
    grid-column: 1 / 6;
    max-height: 500px;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: calc(100% - 1px);
    left: 0;
    width: 100%;
    height: 43px;
    clip-path: polygon(0 0, 0% 100%, 100% 100%);
    background-color: ${brand.primary.alt};
    z-index: 2;
  }

  @media ${from(Device.Desktop)} {
    &::before {
      content: '';
      position: absolute;
      left: calc(100% - 1px);
      top: 0;
      width: 130px;
      height: 100%;
      clip-path: polygon(100% 0, 0 0, 0 100%);
      background-color: ${brand.primary.alt};
      z-index: 1;
    }
  }
`;
const TopSectionDate = styled(m.div).attrs(() => ({
    variants: {
        hidden: {
            x: '-25px',
            opacity: 0,
        },
        visible: {
            x: '0px',
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
    },
})) `
  ${fonts.DaxPro.Bold};
  padding: ${fluid(5, 8)} ${fluid(8, 16)};
  background-color: ${brand.white};
  color: ${brand.primary.alt};
  font-size: ${fluid(18, 23)};
  line-height: ${fluid(23, 29)};
  width: max-content;
  max-width: 100%;
`;
const TopSectionTitle = styled(m.h1).attrs(() => ({
    variants: {
        hidden: {
            x: '-25px',
            opacity: 0,
        },
        visible: {
            x: '0px',
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
    },
})) `
  ${fonts.DaxPro.Bold};
  font-size: ${fluid(26, 38)};
  line-height: ${fluid(32, 48)};
  color: ${brand.white};
  margin: 0;
  max-width: ${fluid(550, 625)};
`;
const TopSectionIntro = styled(m.p).attrs(() => ({
    variants: {
        hidden: {
            x: '-25px',
            opacity: 0,
        },
        visible: {
            x: '0px',
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
    },
})) `
  font-size: ${fluid(17, 20)};
  line-height: ${fluid(21, 25)};
  color: ${brand.white};
  margin: 0;
  max-width: ${fluid(550, 625)};
`;
const TopSectionImage = styled.a `
  overflow: hidden;
  position: relative;

  height: 225px;
  @media ${from(Device.TabletLarge)} {
    height: 260px;
  }
  @media ${from(Device.Desktop)} {
    height: 100%;
    max-height: 500px;
  }

  @media ${until(Device.Desktop)} {
    order: -1;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 2;
    }
  }

  grid-column: 7 / -1;
  @media ${from(Device.DesktopLarge)} {
    grid-column: 6 / -1;
  }

  > img {
    position: absolute;
    bottom: 0;
    right: 0;

    @media ${until(Device.Desktop)} {
      display: none;
    }
  }
`;
const SpinnerWrapper = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            scale: 0.5,
        },
        visible: {
            opacity: 1,
            scale: 1,
        },
    },
})) `
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const TopSectionLazyImageWrapper = styled(m.div).attrs((props) => ({
    initial: 'hidden',
    animate: props['data-loaded'] ? 'visible' : 'hidden',
    variants: {
        hidden: {
            opacity: 0,
            scale: 1.15,
            x: '25px',
        },
        visible: {
            opacity: 1,
            scale: 1,
            x: '0px',
            transition: {
                duration: 0.75,
            },
        },
    },
})) `
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: right center;

    [data-full-image='true'] & {
      object-fit: contain;
    }
  }
`;
const RichTextArea = styled(m.div).attrs(() => ({
    initial: 'hidden',
    animate: 'visible',
    variants: {
        hidden: {
            y: '25px',
            opacity: 0,
        },
        visible: {
            y: '0px',
            opacity: 1,
        },
    },
})) `
  max-width: 1083px;
  margin: auto;

  * {
    margin: 0;
    margin-bottom: ${fluid(24, 48)};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${fonts.DaxPro.Bold};
    font-size: ${fluid(24, 32)};
    line-height: ${fluid(30, 40)};
    color: ${brand.black};
  }

  font-size: ${fluid(18, 20)};
  line-height: ${fluid(23, 25)};
  color: ${brand.black};
  ${fonts.DaxPro.Regular};

  p,
  span {
    white-space: pre-line;
  }

  blockquote {
    display: flex;

    svg {
      width: ${fluid(82, 107)};
      height: ${fluid(97, 127)};
      padding-right: ${fluid(16, 35)};
      flex-shrink: 0;
    }

    div:first-of-type {
      position: relative;
      display: flex;
      align-items: center;

      &::after {
        content: '';
        width: 4px;
        height: 100%;
        position: absolute;
        left: 100%;
        top: 0;
        background: ${brand.primary.alt};
      }
    }

    div:last-of-type {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 16px;
      padding-left: ${fluid(16, 24)};

      *:first-child {
        font-size: ${fluid(18, 24)};
        line-height: ${fluid(23, 30)};
        color: ${brand.black};
        ${fonts.DaxPro.Bold};
      }

      *:last-child {
        font-size: ${fluid(16, 20)};
        line-height: ${fluid(21, 25)};
        color: ${brand.grey.grey40};
        ${fonts.DaxPro.Medium};
      }
    }

    * {
      margin: 0 !important;
    }
  }

  img {
    max-height: ${fluid(202, 620)};
    width: 100%;
    object-fit: contain;
  }

  ul {
    margin: 1.5rem;
    list-style: disc;
  }

  ol {
    margin: 1.5rem;
    list-style: decimal;
  }
`;
export const NewsDetailStyles = {
    Wrapper,
    Container,
    TopSection,
    TopSectionLeftContent,
    TopSectionDate,
    TopSectionTitle,
    TopSectionIntro,
    TopSectionImage,
    RichTextArea,
    TopSectionLazyImageWrapper,
    SpinnerWrapper,
};
